import React, { FC, ReactNode } from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

import {
  TCrmCohorts,
  TCrmSchools,
  TCrmSignal,
  TCrmSummary,
} from '../../../../shared/common/api/endpoints/crmAnalyticsApi';
import styles from '../CrmAnalytics.module.scss';
import {
  formatDays,
  formatMoney,
  formatMoneyShort,
  formatMonths,
  formatNumber,
  formatPercent,
} from '../utils';

interface TTile {
  key: string;
  label: string;
  hint: string;
  value: ReactNode;
  sub?: ReactNode;
  onClick?: () => void;
}

interface Props {
  summary: TCrmSummary | null;
  cohorts: TCrmCohorts | null;
  schools: TCrmSchools | null;
  onlyReal: boolean;
  onOpenSchools: (signal: TCrmSignal) => void;
}

const KpiTiles: FC<Props> = ({ summary, cohorts, schools, onlyReal, onOpenSchools }) => {
  const totals = summary?.totals;
  const current = summary?.current;
  const lifetime = schools?.totals;

  const cash = totals ? totals.cash_real + (onlyReal ? 0 : totals.cash_bonus + totals.cash_excluded) : null;
  const accrual = totals ? totals.accrual_real + (onlyReal ? 0 : totals.accrual_bonus) : null;
  const stoppedPaying = schools?.rows.filter((row) => row.signals.includes('stopped_paying')).length ?? null;

  // Первый ряд — деньги, второй — школы
  const tiles: TTile[] = [
    {
      key: 'cash',
      label: 'Поступления за период',
      hint: onlyReal
        ? 'Реальные платежи: статус «Получен», основной счёт, не бонусное пополнение'
        : 'Все платежи: реальные, бонусные, «Не учитывать» и возвраты',
      value: formatMoneyShort(cash),
      sub: totals && !onlyReal ? `из них реальные ${formatMoneyShort(totals.cash_real)}` : 'по дате платежа',
    },
    {
      key: 'accrual',
      label: 'Начислено за период',
      hint: 'Списания за дни доступа. Только реальные — списано с основного счёта, без бонусного',
      value: formatMoneyShort(accrual),
      sub: 'отработано сервисом',
    },
    {
      key: 'commission',
      label: 'Комиссия партнёров',
      hint: 'Начисленные партнёрам комиссии по платежам за период, по дате платежа. Из выручки не вычитается',
      value: formatMoneyShort(totals?.partner_commission),
      sub: totals && totals.cash_real > 0
        ? `${formatPercent(Math.round(totals.partner_commission / totals.cash_real * 1000) / 10)} от реальных поступлений`
        : null,
    },
    {
      key: 'balance',
      label: 'Деньги на балансах',
      hint: 'Сумма положительных остатков основных счетов: школы заплатили, сервис ещё не отработал',
      value: formatMoneyShort(current?.balance_main),
      sub: 'предоплата, ещё не отработана',
    },
    {
      key: 'ltv',
      label: 'LTV школы, медиана',
      hint: 'Реальные платежи школы за всё время — по школам, которые хоть раз платили',
      value: formatMoney(lifetime?.ltv_median),
      sub: lifetime
        ? `в среднем ${formatMoney(lifetime.ltv_avg)} · платили ${formatNumber(lifetime.paid_ever)} школ`
        : null,
    },
    {
      key: 'lifetime',
      label: 'Платящая жизнь, медиана',
      hint: 'Сколько школа была на оплаченном доступе за всё время — по школам, которые платили. В сервисе — от подключения до последнего дня с доступом',
      value: formatMonths(lifetime?.paid_days_median),
      sub: lifetime
        ? `${formatDays(lifetime.paid_days_median)} · в среднем ${formatMonths(lifetime.paid_days_avg)} · в сервисе ${formatMonths(lifetime.service_days_paid_avg)}`
        : null,
    },
    {
      key: 'paying',
      label: 'Платят сейчас',
      hint: 'Школы с оплаченным доступом в последний день детализации',
      value: formatNumber(current?.statuses.paying),
      sub: totals && current
        ? `в долгу ${formatNumber(current.statuses.debt)} · на начало периода ${formatNumber(totals.paying_start)}`
        : null,
    },
    {
      key: 'conversion',
      label: 'Из теста в покупатели',
      hint: 'Доля школ, подключившихся за период, у которых уже был первый реальный платёж',
      value: formatPercent(cohorts?.totals.conversion),
      sub: cohorts
        ? `${formatNumber(cohorts.totals.paid_ever)} из ${formatNumber(cohorts.totals.connected)} подключившихся`
        : null,
    },
    {
      key: 'new-paying',
      label: 'Начали платить',
      hint: 'Первый реальный платёж школы пришёлся на период. Вернулись — снова платят после перерыва больше 30 дней',
      value: formatNumber(totals?.new_paying),
      sub: totals ? `и вернулись ${formatNumber(totals.returned)}` : null,
    },
    {
      key: 'churn',
      label: 'Ушли за период',
      hint: 'Платили и 30 дней подряд остались без оплаченного доступа. Отток — доля ушедших среди всех, кто платил в периоде. Последние 30 дней ещё досчитываются',
      value: formatNumber(totals?.churned),
      sub: totals ? `отток ${formatPercent(totals.churn_rate)}` : null,
    },
    {
      key: 'stopped',
      label: 'Перестали платить недавно',
      hint: 'Приостановлены меньше 30 дней назад — ещё можно вернуть. Нажмите, чтобы открыть список',
      value: formatNumber(stoppedPaying),
      sub: 'открыть список →',
      onClick: () => onOpenSchools('stopped_paying'),
    },
    {
      key: 'not-paying',
      label: 'Работают, но не платят',
      hint: 'Приостановлены дольше 30 дней: выручка выше 100 тыс. ₽, данные из GetCourse приходят, денег нет. Нажмите, чтобы открыть список',
      value: formatNumber(lifetime?.works_not_paying),
      sub: lifetime
        ? `их выручка за 30 дней ${formatMoneyShort(lifetime.works_not_paying_revenue)} →`
        : null,
      onClick: () => onOpenSchools('works_not_paying'),
    },
  ];

  return (
    <div className={styles.tiles}>
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={tile.onClick ? `${styles.tile} ${styles.tileClickable}` : styles.tile}
          onClick={tile.onClick}
          role={tile.onClick ? 'button' : undefined}
        >
          <div className={styles.tileLabel}>{tile.label}</div>
          <Tooltip title={tile.hint}>
            <QuestionCircleOutlined className={styles.tileHint} />
          </Tooltip>
          <div className={styles.tileValue}>{tile.value}</div>
          {tile.sub && <div className={styles.tileSub}>{tile.sub}</div>}
        </div>
      ))}
    </div>
  );
};

export default KpiTiles;
